import { Dispatch, SetStateAction, useMemo, useState } from "react";

import { differenceInDays, isBefore, isSameDay, set } from "date-fns";
import { Button, Dropdown, Grid } from "semantic-ui-react";
import styled from "styled-components";

import { dateStart, formatDate } from "simplydo/core/dates";

import { useAppSelector } from "store";

import { Sortable } from "components/lib/dnd/Sortable";

import { BackgroundEvent } from "./Events/CalendarBackgroundEvent";
import { ForegroundEvent } from "./Events/CalendarForegroundEvent";
import {
  CalendarEventWithDates,
  CellAction,
  EventWithIndex,
} from "./calendar-types";

const StyledItemButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  margin-left: 10px;
  padding: 3px !important;
`;

const EventList = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
  padding-bottom: 8px;
`;

type BaseCalendarCellProps = {
  key: string;
  dayDate: Date;
  daysIntoCalendar: number;
  totalDays: number;
  dayNumOfTheWeek: number;
  weekNum: number;
  events: EventWithIndex[];
  onSelectEvent: Dispatch<SetStateAction<EventWithIndex | null>>;
  selectedEvent: EventWithIndex | null;
  leaveEvent?: (eventId: string) => void;
  deleteEvent: (eventId: string, eventType: string) => void;
  setEditingEvent: Dispatch<
    SetStateAction<Partial<CalendarEventWithDates> | null>
  >;
  editable: boolean;
  showMembers: boolean;
  cellActions?: Array<CellAction>;
};

const BaseCalendarCell = ({
  dayDate,
  daysIntoCalendar,
  totalDays,
  dayNumOfTheWeek,
  events,
  onSelectEvent,
  selectedEvent,
  leaveEvent,
  deleteEvent,
  setEditingEvent,
  editable,
  showMembers,
  cellActions,
}: BaseCalendarCellProps) => {
  const user = useAppSelector((state) => state.user);
  const isToday = isSameDay(dayDate, new Date());
  const isWeekendDay = dayNumOfTheWeek === 5 || dayNumOfTheWeek === 6;
  const isPaddingBeforeStartDay = daysIntoCalendar < 0;
  const isPaddingAfterEndDay = daysIntoCalendar >= totalDays;
  const [isMouseOver, setIsMouseOver] = useState<Date | null>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const isPadding = isPaddingBeforeStartDay || isPaddingAfterEndDay;

  const { foregroundEvents, backgroundEvents } = useMemo<{
    foregroundEvents: EventWithIndex[];
    backgroundEvents: EventWithIndex[];
  }>(() => {
    let foregroundIndex = 0;
    return events.reduce(
      (
        acc: {
          foregroundEvents: EventWithIndex[];
          backgroundEvents: EventWithIndex[];
        },
        event,
      ) => {
        if (event.eventType === "background") {
          acc.backgroundEvents.push(event);
        } else {
          const dummiesToInsert = event.index - foregroundIndex;
          for (let i = 0; i < dummiesToInsert; i++) {
            acc.foregroundEvents.push({ eventType: "dummy" } as EventWithIndex);
          }
          foregroundIndex = event.index + 1;

          const daysFromStart = differenceInDays(
            dayDate,
            dateStart(event.from),
          );
          event.daysFromStart = daysFromStart;

          acc.foregroundEvents.push({ ...event, daysFromStart });
        }

        return acc;
      },
      { foregroundEvents: [], backgroundEvents: [] },
    );
  }, [events, dayDate]);

  return (
    <Grid.Column
      style={{
        padding: 0,
        paddingBottom: 0,
        background:
          isToday ? "#e6f7ff"
          : isPadding ? "#e2e2e2"
          : isWeekendDay ? "#f9f9f9"
          : "white",
      }}
      onMouseOver={() => setIsMouseOver(dayDate)}
      onMouseLeave={() => setIsMouseOver(null)}
    >
      <Sortable
        disabled
        id={`day-${daysIntoCalendar}`}
        index={daysIntoCalendar}
        type="day"
        accept={["event"]}
      >
        <div style={{ flex: 1, height: "100%", paddingBottom: 20 }}>
          <div style={{ position: "relative", zIndex: 1, paddingLeft: 4 }}>
            <span style={{ color: "grey" }}>
              {formatDate(dayDate, "MMM d")}{" "}
              {isPadding || isWeekendDay ? null : (
                <b>Day {daysIntoCalendar + 1}</b>
              )}
            </span>
          </div>

          <EventList>
            {foregroundEvents.map((event, index) => (
              <ForegroundEvent
                key={event.id}
                index={index}
                event={event}
                currDate={dayDate}
                onSelectEvent={onSelectEvent}
                selected={selectedEvent?.id === event.id}
                deleteEvent={deleteEvent}
                leaveEvent={leaveEvent}
                setEditingEvent={setEditingEvent}
                editable={editable}
                showMembers={showMembers}
                isOutsideRange={isPadding}
              />
            ))}
          </EventList>

          {backgroundEvents.map((event) => (
            <BackgroundEvent
              key={event.id}
              event={event}
              currDate={dayDate}
              setEditingEvent={setEditingEvent}
            />
          ))}
          {(isMouseOver || isOpen) && !isPadding && editable && (
            <Dropdown
              icon={null}
              pointing="top right"
              style={{ position: "absolute", bottom: 5, right: 5 }}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              trigger={
                <StyledItemButton size="mini" icon="add" content="Add item" />
              }
            >
              <Dropdown.Menu>
                <Dropdown.Item
                  text="Add an event"
                  onClick={() =>
                    setEditingEvent({
                      type: "event",
                      from: set(new Date(dayDate), {
                        hours: 9,
                        minutes: 0,
                        seconds: 0,
                        milliseconds: 0,
                      }),
                      to: set(new Date(dayDate), {
                        hours: 10,
                        minutes: 0,
                        seconds: 0,
                        milliseconds: 0,
                      }),
                      color: "#3f88d2",
                      members: [user._id],
                      // // @ts-ignore
                      ownerMembers: [user],
                      organiserName: user.profile.fullName,
                      organiserEmail: user.emails[0].address,
                      notifications: [{ value: 10, unit: "minutes" }],
                    })
                  }
                />
                {cellActions?.map((action) =>
                  (
                    action?.onlyVisibleInRange &&
                    isBefore(
                      dayDate.setHours(1, 0, 0, 0),
                      new Date().setHours(0, 0, 0, 0),
                    )
                  ) ?
                    null
                  : <Dropdown.Item
                      key={action?.name}
                      text={action?.name}
                      onClick={() => {
                        const eventData = action.action(
                          dayDate as unknown as string,
                        );
                        setEditingEvent(eventData as Event);
                      }}
                    />,
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </Sortable>
    </Grid.Column>
  );
};

export default BaseCalendarCell;
